import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import PublicLayout from '../../components/layout/PublicLayout';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { authAPI } from '../../services/api';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  
  const { state } = location;
    const { email, role } = state || {};

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    if (!email) {
      toast.error('Session expired. Please start again.');
      navigate('/forgot-password');
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await authAPI.resetPassword(email, data.password, data.confirmPassword, role);
      if (response.data.success) {
        toast.success('Password reset successfully!');
        navigate('/login', { state: { role } });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to reset password';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (!email) {
    return (
      <PublicLayout title="Reset Password" showBack>
        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          <div className="card">
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Icon name="alert-circle" size={48} color="var(--red)" />
              <h3 style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: '700',
                fontSize: '20px',
                color: 'var(--text)',
                marginBottom: '8px'
              }}>
                Invalid Session
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px' }}>
                Please start the password reset process again.
              </p>
              <Button
                variant="primary"
                size="md"
                onClick={() => navigate('/forgot-password')}
                style={{ width: '100%' }}
              >
                Back to Forgot Password
              </Button>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout title="Reset Password" showBack>
      <div style={{ maxWidth: '400px', margin: '0 auto' }}>
        <div className="card">
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'var(--primary-light)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Icon name="lock" size={32} color="var(--primary)" />
            </div>
            <h2 style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: '700',
              fontSize: '24px',
              color: 'var(--text)',
              marginBottom: '8px'
            }}>
              Set New Password
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
              Please enter your new password below.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* New Password */}
            <div>
              <label className="label">New Password *</label>
              <input
                {...register('password', { 
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
                type="password"
                placeholder="Enter new password"
                style={{
                  width: '100%',
                  borderColor: errors.password ? 'var(--red)' : 'var(--border)'
                }}
              />
              {errors.password && (
                <p className="text-red-500">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="label">Confirm New Password *</label>
              <input
                {...register('confirmPassword', { 
                  required: 'Please confirm your password',
                  validate: value => value === password || 'Passwords do not match'
                })}
                type="password"
                placeholder="Confirm new password"
                style={{
                  width: '100%',
                  borderColor: errors.confirmPassword ? 'var(--red)' : 'var(--border)'
                }}
              />
              {errors.confirmPassword && (
                <p className="text-red-500">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={submitting}
              style={{ width: '100%', padding: '12px' }}
            >
              Reset Password
            </Button>

            {/* Back to Login */}
            <div style={{ textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => navigate('/login', { state: { role } })}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--primary)',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                ← Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ResetPassword;
